import Notification from '@rio-cloud/rio-uikit/Notification';
import NoData from 'components/content/NoData';
import ErrorDialog from 'components/overlays/ErrorDialog';
import type { SortConfig, TableColumn } from 'components/table';
import { DataTable, getNextSortConfig, sortData } from 'components/table';
import { SystemAdminDialogTypes } from 'helper/types';
import { useCan } from 'hooks/useCan';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeleteUserByIdMutation, useListUsersQuery, useResetPasswordByIdMutation } from 'services/user';
import {
    ActiveSidebar,
    getActiveSidebar,
    getActiveUser,
    getSystemAdminOpenDialog,
    setActiveSidebar,
    setActiveUser,
    setSystemAdminOpenDialog,
    setUserDialog,
} from 'store/slices/settingsPageSlice';

import DateOrNoData from './DateOrNoData';
import DeleteUserDialog from './DeleteUserDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import SystemUsersRowStatus from './SystemUsersRowStatus';
import { getUserFullName } from './utils';

import { Status, type User } from 'API';

import './_styles.scss';

interface Props {
    openDialogInfo: () => void;
}

const SystemUsersTable = ({ openDialogInfo }: Props) => {
    const dispatch = useDispatch();

    const systemAdminOpenDialog = useSelector(getSystemAdminOpenDialog);
    const resetPasswordErrorDialogIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.RESET_PASSWORD_ERROR;
    const deleteUserErrorDialogIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.DELETE_USER_ERROR;
    const [resetUserPassword, { data: resetPasswordData, isLoading: isResetPasswordLoading }] =
        useResetPasswordByIdMutation({
            fixedCacheKey: 'resetPasswordById',
        });
    const [deleteUserById, { data: deleteUserData, isLoading: isDeleteUserLoading }] = useDeleteUserByIdMutation({
        fixedCacheKey: 'deleteUserById',
    });

    const {
        data,
        isLoading,
        isFetching: isSystemUsersFetching,
        isError,
        refetch,
    } = useListUsersQuery('ListUsers', {
        refetchOnMountOrArgChange: true,
    });
    const systemUsers = data ?? [];

    const renderTextOrNoData = (value: unknown) =>
        value ? String(value) : <NoData text='No data' className='DateOrNoData__nodata' />;

    const columns = useMemo<TableColumn<User>[]>(
        () => [
            {
                key: 'firstName',
                title: 'First name',
                dataIndex: 'firstName',
                sortable: true,
            },
            {
                key: 'lastName',
                title: 'Last name',
                dataIndex: 'lastName',
                sortable: true,
            },
            {
                key: 'email',
                title: 'E-mail',
                dataIndex: 'email',
                sortable: true,
            },
            {
                key: 'phone',
                title: 'Phone',
                dataIndex: 'phone',
                sortable: true,
                render: value => renderTextOrNoData(value),
            },
            {
                key: 'country',
                title: 'Country',
                dataIndex: 'country',
                sortable: true,
                render: value => renderTextOrNoData(value),
            },
            {
                key: 'city',
                title: 'City',
                dataIndex: 'city',
                sortable: true,
                render: value => renderTextOrNoData(value),
            },
            {
                key: 'teams',
                title: 'Team',
                dataIndex: 'teams',
                render: (_, user) => renderTextOrNoData(user?.teams?.map(team => team.name).join(', ')),
            },
            {
                key: 'roles',
                title: (
                    <>
                        Role
                        <span
                            className='rioglyph rioglyph-info-sign text-15 ml-5 cursor-pointer text-gray-dark'
                            onClick={openDialogInfo}
                        />
                    </>
                ),
                dataIndex: 'roles',
                render: (_, user) => renderTextOrNoData(user?.roles?.map(role => role.name).join(', ')),
            },
            {
                key: 'status',
                title: 'Status',
                dataIndex: 'status',
                sortable: true,
                render: (_, user) => <SystemUsersRowStatus status={user.status} />,
            },
            {
                key: 'createdAt',
                title: 'Created',
                dataIndex: 'createdAt',
                sortable: true,
                render: (_, user) => <DateOrNoData value={user.createdAt} />,
                sortValue: user => new Date(user.createdAt ?? ''),
            },
            {
                key: 'updatedAt',
                title: 'Updated',
                dataIndex: 'updatedAt',
                sortable: true,
                render: (_, user) => <DateOrNoData value={user.updatedAt} />,
                sortValue: user => new Date(user.updatedAt ?? ''),
            },
        ],
        [openDialogInfo]
    );

    const activeSidebar = useSelector(getActiveSidebar);
    const activeUser = useSelector(getActiveUser);

    const [sortConfig, setSortConfig] = useState<SortConfig<string>>({ key: 'firstName', dir: 'asc' });

    const sortedUsers = useMemo(() => sortData(systemUsers, sortConfig, columns), [systemUsers, sortConfig, columns]);

    const { can: canEditUsers } = useCan({ type: 'users', action: 'edit' });
    const { can: canDeleteUsers } = useCan({ type: 'users', action: 'delete' });

    // TODO: extract all the dialog/action stuff into one or more hook/helper?
    const closeDialog = () => {
        dispatch(setSystemAdminOpenDialog(undefined));
    };

    const closeFlow = () => {
        const fullName = getUserFullName(activeUser) ?? 'user';
        closeDialog();
        Notification.success(<p>They will find instructions in the email to create a new password</p>, {
            title: `Password reset email sent to ${fullName}`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const closeDeleteFlow = (deletedUser: User) => {
        const fullName = getUserFullName(deletedUser) ?? 'user';
        dispatch(setActiveUser(undefined));
        closeDialog();
        Notification.success(<p>The user will be logged out within 5 minutes if currently logged in</p>, {
            title: `${fullName} has been deleted`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const refreshTable = async () => {
        await refetch()
            .unwrap()
            .then(() => closeFlow());
    };

    const submitResetPassword = async () => {
        if (!activeUser) {
            console.error('No user selected');
            return;
        }

        await resetUserPassword({ input: activeUser.id })
            .unwrap()
            .then(res => {
                if (res && 'errorCode' in res) {
                    throw new Error(`Error code found: ${res.errorCode}`);
                }

                if (res.statusCode !== 200) {
                    throw new Error('Error status code');
                }

                return refreshTable();
            })
            .catch(error => {
                console.error(error);
                dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.RESET_PASSWORD_ERROR));
            });
    };

    const submitDeleteUser = async () => {
        if (!activeUser) {
            console.error('No user selected');
            return;
        }

        await deleteUserById({ input: activeUser.id })
            .unwrap()
            .then(res => {
                if (res && 'errorCode' in res) {
                    throw new Error(`Error code found: ${res.errorCode}`);
                }

                if (res.statusCode !== 204) {
                    throw new Error('Error status code');
                }

                closeDeleteFlow(activeUser);
            })
            .catch(error => {
                console.error(error);
                dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.DELETE_USER_ERROR));
            });
    };

    const generateUserActions = (user: User) => [
        ...(canEditUsers
            ? [
                  {
                      id: 'edit',
                      title: 'Edit',
                      icon: 'rioglyph-pencil',
                      onSelect: () => {
                          dispatch(setActiveUser(user));
                          dispatch(setUserDialog({ user, showUserDialog: true }));
                      },
                  },
              ]
            : []),
        ...(canEditUsers && user.status === Status.ACTIVE
            ? [
                  {
                      id: 'reset-password',
                      title: 'Reset password',
                      icon: 'rioglyph-key',
                      onSelect: () => {
                          dispatch(setActiveUser(user));
                          dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.RESET_PASSWORD_DIALOG));
                      },
                  },
              ]
            : []),
        ...(canDeleteUsers
            ? [
                  {
                      id: 'delete',
                      title: 'Delete',
                      icon: 'rioglyph-trash',
                      variant: 'danger' as const,
                      onSelect: () => {
                          dispatch(setActiveUser(user));
                          dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.DELETE_USER_DIALOG));
                      },
                  },
              ]
            : []),
    ];

    return (
        <div className='display-flex flex-column' data-testid='system-users-table-wrapper'>
            <DataTable
                data-testid='system-users-table'
                stickyHeader
                columns={columns}
                dataSource={sortedUsers}
                rowKey='id'
                activeRowKey={activeSidebar === ActiveSidebar.USERS ? activeUser?.id : undefined}
                sortConfig={sortConfig}
                onSortChange={key => setSortConfig(prev => getNextSortConfig(prev, key))}
                onRowClick={user => {
                    dispatch(setActiveSidebar(ActiveSidebar.USERS));
                    dispatch(setActiveUser(user));
                }}
                isLoading={isLoading}
                loadMore={{
                    // There is no pagination support here yet, but the table footer will look like the other tables that have pagination.
                    loaded: systemUsers.length,
                    total: systemUsers.length,
                }}
                isError={isError}
                actionColumn={{
                    ariaLabel: user => `Actions for ${getUserFullName(user) ?? user.email}`,
                    disabled: !canEditUsers && !canDeleteUsers,
                    actions: user => generateUserActions(user),
                }}
                error={{
                    header: 'Failed to load users',
                    message: 'If this issue persists, please contact the Control Center support team.',
                    onClick: () => refetch(),
                }}
                empty={{
                    title: 'No users yet, but they will appear here',
                    body: 'As of now, there are no users in the Control Center. Add a user to assign roles and add to teams.',
                }}
            />
            <ResetPasswordDialog
                onConfirm={submitResetPassword}
                onCancel={closeDialog}
                isLoading={isSystemUsersFetching || isResetPasswordLoading}
            />
            <DeleteUserDialog
                onConfirm={submitDeleteUser}
                onCancel={closeDialog}
                isLoading={isSystemUsersFetching || isDeleteUserLoading}
            />
            <ErrorDialog
                show={resetPasswordErrorDialogIsOpen}
                onConfirm={closeDialog}
                errorStatus={resetPasswordData?.statusCode}
            />
            <ErrorDialog
                show={deleteUserErrorDialogIsOpen}
                onConfirm={closeDialog}
                errorStatus={deleteUserData?.statusCode}
            />
        </div>
    );
};

export default SystemUsersTable;
